/**
 * PROVIDERS — the network identity directory.
 *
 * Each row is an identity anchor, not a company profile: the label is the
 * normalized matching key, and what matters operationally is how many of
 * this tenant's records are CONFIRMED against it versus still awaiting a
 * human decision. Those decisions are surfaced here because an unconfirmed
 * match contributes no facts to any passport or match.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import exchangeApi from "../services/exchangeService";
import { classifyWorkflowError } from "../lib/workflowErrors";
import { hasCapability } from "../lib/capabilities";
import { createSequencer } from "../lib/sequencer";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  SectionLoading,
  SectionEmpty,
  SectionError,
  FeatureDisabled,
  NotEntitled,
  NoAccess,
} from "../components/workflow/WorkflowStates";
import { linkStateDisplay } from "../features/exchange/exchangePresentation";
import { Search, IdCard, ChevronRight, Users, ScanSearch, Loader2 } from "lucide-react";

const STATE_TONE = {
  verified: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  proposed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  bad: "bg-red-500/20 text-red-400 border-red-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
};


const ExchangeProvidersPage = () => {
  const navigate = useNavigate();
  const { user, capabilities } = useAuth();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const seq = useRef(createSequencer()).current;

  // Served by the session, not mirrored from routes/exchange.py.
  const canPropose = hasCapability(capabilities, "exchange.propose");

  const load = useCallback(async () => {
    const ticket = seq.next();
    setLoading(true);
    setError(null);
    try {
      const result = await exchangeApi.listProviders(submitted);
      if (seq.isCurrent(ticket)) setData(result);
    } catch (err) {
      if (seq.isCurrent(ticket)) {
        setData(null);
        setError(classifyWorkflowError(err));
      }
    } finally {
      if (seq.isCurrent(ticket)) setLoading(false);
    }
  }, [submitted, seq]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Identity links do not appear on their own — something has to scan the
   * tenant's records and propose them. This is that action, and it is
   * deliberately an explicit operator step: it writes PROPOSED_MATCH rows and
   * nothing else, so it can never merge two organizations by itself.
   */
  const runScan = async () => {
    setScanning(true);
    try {
      const result = await exchangeApi.proposeIdentityLinks();
      setScanResult(result);
      setScanOpen(false);
      const created = (result.createdLinks || []).length;
      toast.success(
        created === 0
          ? "Scan complete — no new identity matches to propose."
          : `Scan complete — ${created} identity match${created === 1 ? "" : "es"} proposed for review.`
      );
      load();
    } catch (err) {
      const classified = classifyWorkflowError(err);
      toast.error(
        classified.kind === "conflict"
          ? "Another scan recorded some of these matches first. Reloading the current state."
          : classified.message
      );
      if (classified.kind === "conflict") load();
    } finally {
      setScanning(false);
    }
  };

  const facets = data?.stateFacets || {};
  const awaitingDecision =
    (facets.PROPOSED_MATCH || 0) + (facets.DISPUTED || 0) + (facets.UNRESOLVED || 0);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Providers
          </h1>
          <p className="text-muted-foreground mt-1">
            Network identities your records are linked to.
          </p>
          {/* The anchor's displayName is the normalized matching key OMNΛS
              groups records under — lowercased, punctuation stripped, the
              corporate suffix dropped. It is NOT a registered company name,
              and presenting it as one would attribute a name nobody stated. */}
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Each row is named by the matching key records are grouped under, not by a
            registered company name. Open a provider to see the names its linked records
            actually carry.
          </p>
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(query);
          }}
        >
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search provider name…"
              className="pl-8 h-9 w-56"
              aria-label="Search providers by name"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9">
            Search
          </Button>
          {canPropose && !["disabled", "not-entitled", "no-access"].includes(error?.kind) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setScanOpen(true)}
            >
              <ScanSearch className="w-4 h-4 mr-1.5" />
              Scan for matches
            </Button>
          )}
        </form>
      </div>

      {/* What the last scan actually covered. A bounded run that reads as
          complete is exactly what the backend's truncation flags exist to
          prevent, so they are stated rather than swallowed. */}
      {scanResult && (
        <Card className="border-border bg-muted/20">
          <CardContent className="py-3 px-4 space-y-1 text-xs text-muted-foreground">
            <p className="text-foreground font-medium">
              Last scan: {(scanResult.createdLinks || []).length} new match
              {(scanResult.createdLinks || []).length === 1 ? "" : "es"} proposed
              {typeof scanResult.alreadyLinkedCount === "number"
                ? `, ${scanResult.alreadyLinkedCount} record${
                    scanResult.alreadyLinkedCount === 1 ? "" : "s"
                  } already linked`
                : ""}
              .
            </p>
            {scanResult.scannedCounts && (
              <p>
                Read {scanResult.scannedCounts.company ?? 0} company,{" "}
                {scanResult.scannedCounts.vendor ?? 0} vendor and{" "}
                {scanResult.scannedCounts.subcontractor_company ?? 0} subcontractor records.
              </p>
            )}
            {(scanResult.scanTruncated || scanResult.fuzzyTruncated) && (
              <p className="text-amber-400">
                This scan was incomplete —{" "}
                {scanResult.scanTruncated
                  ? "more records exist than it read"
                  : "more name pairs existed than it compared"}
                . Matches beyond that bound were not proposed.
              </p>
            )}
            <p>Proposals confirm nothing. Open a provider to confirm or dispute each one.</p>
          </CardContent>
        </Card>
      )}

      {error ? (
        error.kind === "not-entitled" ? (
          <NotEntitled feature="The Exchange" message={error.message} />
        ) : error.kind === "disabled" ? (
          <FeatureDisabled
            feature="The Exchange"
            hint="The provider network is built but not enabled for this environment."
          />
        ) : error.kind === "no-access" ? (
          <NoAccess message={error.message} />
        ) : (
          <SectionError error={error} onRetry={load} label="Providers failed to load" />
        )
      ) : loading ? (
        <SectionLoading label="Loading the provider network…" />
      ) : !data || (data.providers || []).length === 0 ? (
        <SectionEmpty
          icon={IdCard}
          title={submitted ? "No provider matches that search" : "No linked providers yet"}
          hint={
            submitted
              ? "Try a different name. Search matches the display name and the normalized matching key."
              : "Providers appear here once your vendor, subcontractor and company records are linked to network identities."
          }
        />
      ) : (
        <>
          {awaitingDecision > 0 && (
            <Card className="border-blue-500/30 bg-blue-500/5">
              <CardContent className="py-3 px-4 text-sm">
                {/* The backend applies `q` BEFORE it accumulates the facets,
                    so this figure covers the search results — not the tenant
                    — whenever a search is active. */}
                <span className="font-medium">{awaitingDecision}</span> identity link
                {awaitingDecision === 1 ? "" : "s"}{" "}
                {submitted ? "among these search results are" : "across your records are"} not
                confirmed. An unconfirmed link contributes no facts to a passport or a match —
                open a provider to confirm or dispute it.
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.providers.map((provider) => (
              <Card key={provider.id} className="border-border">
                <CardContent className="py-3 px-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="text-sm font-medium font-mono truncate"
                      title="The normalized matching key for this identity anchor — not a registered company name."
                    >
                      {provider.displayName}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs shrink-0"
                      onClick={() => navigate(`/exchange/providers/${provider.id}`)}
                    >
                      Passport
                      <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(provider.linkStates || {}).map(([state, count]) => {
                      const display = linkStateDisplay(state);
                      return (
                        <Badge
                          key={state}
                          variant="outline"
                          className={`${STATE_TONE[display.tone] || STATE_TONE.unknown} font-normal text-[10px]`}
                        >
                          {display.label}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* `truncated` has two causes and they mean different things: the
              organization list hit its cap (count < totalCount), or the
              underlying link scan hit its row bound (counts agree, but the
              scan itself was incomplete). Reporting the first wording for the
              second case reads as a contradiction — "N of N, but more exist". */}
          {data.truncated &&
            (data.count < data.totalCount ? (
              <p className="text-[11px] text-muted-foreground">
                Showing {data.count} of {data.totalCount} providers found. Narrow the search to
                see the rest.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                The identity-link scan reached its row bound, so providers beyond it are not in
                this list. Narrow the search to reach them.
              </p>
            ))}
        </>
      )}

      <Dialog open={scanOpen} onOpenChange={(open) => !scanning && setScanOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Scan records for identity matches</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This reads your own company, vendor and subcontractor records and proposes where
              two of them look like the same organization. It matches on normalized name,
              website domain and close name similarity.
            </p>
            <p className="text-sm text-muted-foreground">
              Nothing is merged and nothing is confirmed. Every match it finds is recorded as a
              proposal for a person to confirm or dispute, and unconfirmed matches contribute no
              facts anywhere.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setScanOpen(false)}
                disabled={scanning}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={runScan} disabled={scanning}>
                {scanning && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Run scan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExchangeProvidersPage;
